import React from 'react';
import { useTranslation } from 'react-i18next';

// Ion Icons 스크립트 추가 (index.js와 동일)
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'ion-icon': any;
    }
  }
}

interface CardPreviewProps {
  cardData: {
    name: string;
    company?: string;
    position?: string;
    email?: string;
    phone?: string;
    website?: string;
    customUrl?: string;
    customPath?: string;
    customDomain?: string;
    shareUrl?: string;
    photoUrl?: string;
    portfolioImages?: string[];
  };
  cardButtonColor: string;
  cardBackgroundColor: string;
}

const CardPreview: React.FC<CardPreviewProps> = ({ cardData, cardButtonColor, cardBackgroundColor }) => {
  const { t } = useTranslation();
  
  // index.js와 동일한 아이콘 매핑
  const iconMap = {
    // brands
    instagram: "logo-instagram",
    threads: "at-outline", // Threads icon as @ symbol
    youtube: "logo-youtube",
    linkedin: "logo-linkedin",
    facebook: "logo-facebook",
    tiktok: "logo-tiktok",
    kakaotalk: "chatbubble-outline",
    whatsapp: "logo-whatsapp",
    twitter: "logo-twitter",
    // solid icons
    email: "mail-outline",
    phone: "call-outline",
    website: "globe-outline",
    sms: "chatbubble-ellipses-outline",
    calendar: "calendar-outline",
    download: "arrow-down-outline",
    copy: "copy-outline",
    share: "share-social-outline",
    plus: "person-add-outline",
    review: "chatbox-ellipses-outline",
    address: "location-outline",
  };

  const getIconHtml = (key: string, size = 24) =>
    iconMap[key as keyof typeof iconMap] || "ellipse-outline";
    
  // 색상이 밝은지 어두운지 판단하는 함수
  const isColorLight = (color: string) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155; // index.js와 동일한 임계값
  };

  const isBgLight = isColorLight(cardBackgroundColor);
  const primaryTextColor = isBgLight ? '#000000' : '#FFFFFF';
  const secondaryTextColor = isBgLight ? '#666666' : '#CCCCCC';
  const buttonTextColor = isColorLight(cardButtonColor) ? '#000000' : '#FFFFFF';

  return (
    <div 
      className="card-preview"
      style={{
        background: cardBackgroundColor,
        color: primaryTextColor,
        minHeight: '560px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        lineHeight: 1.5,
        padding: '1.5rem',
        borderRadius: '1rem',
        boxShadow: '0 18px 45px rgba(15, 23, 42, 0.12)',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)',
        maxWidth: '21rem',
        width: '100%',
        margin: '0 auto'
      }}
    >
      {/* 프로필 이미지 */}
      {cardData.photoUrl && (
        <div style={{ marginBottom: '2rem' }}>
          <img
            src={cardData.photoUrl}
            alt="Profile"
            style={{
              width: '8rem',
              height: '8rem',
              borderRadius: '50%',
              objectFit: 'cover',
              border: `3px solid ${cardButtonColor}33`,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
            }}
          />
        </div>
      )}

      {/* 이름 */}
      <h1 style={{
        fontSize: '1.5rem',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '1rem',
        color: primaryTextColor
      }}>
        {cardData.name || t('common.noName', 'No Name')}
      </h1>

      {/* 직책 */}
      {cardData.position && (
        <p style={{
          fontSize: '1.125rem',
          textAlign: 'center',
          color: secondaryTextColor,
          marginBottom: '0.25rem'
        }}>
          {cardData.position}
        </p>
      )}

             {/* 회사 */}
       {cardData.company && (
         <p style={{
           fontSize: '1rem',
           textAlign: 'center',
           color: secondaryTextColor,
           marginBottom: '1rem'
         }}>
           {cardData.company}
         </p>
       )}

                   {/* URL Display - following app logic */}
            {(() => {
              let displayUrl = '';
              if (cardData.customPath) {
                displayUrl = `https://scanvcf.com/${cardData.customPath}`;
              } else if (cardData.customDomain) {
                displayUrl = `https://${cardData.customDomain}`;
              } else if (cardData.shareUrl) {
                displayUrl = cardData.shareUrl;
              } else {
                displayUrl = 'no-url';
              }
              
              return displayUrl !== 'no-url' ? (
           <div style={{
             marginBottom: '1.25rem',
             padding: '0.75rem 1rem',
             background: `${cardButtonColor}15`,
             borderRadius: '0.5rem',
             border: `1px solid ${cardButtonColor}30`,
             textAlign: 'center'
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               gap: '0.5rem',
               marginBottom: '0.25rem'
             }}>
               <ion-icon name="globe-outline" style={{ fontSize: '0.875rem', color: primaryTextColor }}></ion-icon>
               <span style={{ 
                 fontSize: '0.875rem', 
                 fontWeight: '600',
                 color: primaryTextColor 
               }}>
                 {t('common.website', 'URL')}:
               </span>
             </div>
             <p style={{
               fontSize: '0.75rem',
               color: secondaryTextColor,
               wordBreak: 'break-all',
               margin: 0
             }}>
               {displayUrl}
             </p>
           </div>
         ) : null;
       })()}

      {/* 연락처 버튼들 */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem',
        margin: '1.25rem 0 1rem 0',
        padding: '0.5rem 0'
      }}>
        {cardData.email && (
          <button style={{
            width: '3.25rem',
            height: '3.25rem',
            borderRadius: '1rem',
            background: `${cardButtonColor}22`,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            margin: '0 0.5rem'
          }}>
            <ion-icon name={getIconHtml('email')} style={{ fontSize: '24px', color: cardButtonColor }}></ion-icon>
          </button>
        )}
        {cardData.phone && (
          <button style={{
            width: '3.25rem',
            height: '3.25rem',
            borderRadius: '1rem',
            background: `${cardButtonColor}22`,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            margin: '0 0.5rem'
          }}>
            <ion-icon name={getIconHtml('phone')} style={{ fontSize: '24px', color: cardButtonColor }}></ion-icon>
          </button>
        )}
        {cardData.website && (
          <button style={{
            width: '3.25rem',
            height: '3.25rem',
            borderRadius: '1rem',
            background: `${cardButtonColor}22`,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            margin: '0 0.5rem'
          }}>
            <ion-icon name={getIconHtml('website')} style={{ fontSize: '24px', color: cardButtonColor }}></ion-icon>
          </button>
        )}
      </div>

      {/* 액션 버튼 */}
      <button style={{
        width: '100%',
        padding: '0.75rem 1rem',
        borderRadius: '0.75rem',
        background: cardButtonColor,
        color: buttonTextColor,
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        margin: '1rem 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'all 0.3s ease',
        fontSize: '1rem'
      }}>
        <ion-icon name={getIconHtml('plus')} style={{ fontSize: '20px', marginRight: '0.5rem' }}></ion-icon>
        {t('common.addContact', 'Add Contact')}
      </button>

      {/* 소셜 미디어 버튼들 */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '1.5rem',
        width: '100%',
        maxWidth: '350px',
        margin: '1rem auto',
        padding: '1rem 0.625rem'
      }}>
        <button style={{
          width: '3.5rem',
          height: '3.5rem',
          borderRadius: '1.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          flex: '0 0 calc(25% - 1.5rem)',
          minWidth: '3.5rem',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <ion-icon name={getIconHtml('instagram')} style={{ fontSize: '28px', color: primaryTextColor }}></ion-icon>
        </button>
        <button style={{
          width: '3.5rem',
          height: '3.5rem',
          borderRadius: '1.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          flex: '0 0 calc(25% - 1.5rem)',
          minWidth: '3.5rem',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <ion-icon name={getIconHtml('kakaotalk')} style={{ fontSize: '28px', color: primaryTextColor }}></ion-icon>
        </button>
        <button style={{
          width: '3.5rem',
          height: '3.5rem',
          borderRadius: '1.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          flex: '0 0 calc(25% - 1.5rem)',
          minWidth: '3.5rem',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <ion-icon name={getIconHtml('share')} style={{ fontSize: '28px', color: primaryTextColor }}></ion-icon>
        </button>
        <button style={{
          width: '3.5rem',
          height: '3.5rem',
          borderRadius: '1.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          flex: '0 0 calc(25% - 1.5rem)',
          minWidth: '3.5rem',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <ion-icon name={getIconHtml('copy')} style={{ fontSize: '28px', color: primaryTextColor }}></ion-icon>
        </button>
      </div>

      {/* 포트폴리오 갤러리 */}
      {cardData.portfolioImages && cardData.portfolioImages.length > 0 && (
        <div style={{
          marginTop: '2rem',
          width: '100%'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            textAlign: 'center',
            marginBottom: '1rem',
            color: primaryTextColor
          }}>
            {t('common.portfolio', 'Portfolio')}
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem',
            width: '100%'
          }}>
            {cardData.portfolioImages.map((imageUrl, index) => (
              <div key={index} style={{
                aspectRatio: '9/5',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                border: `1px solid ${cardButtonColor}33`
              }}>
                <img
                  src={imageUrl}
                  alt={`${t('common.portfolio', 'Portfolio')} ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 미리보기 라벨 */}
      <div style={{
        marginTop: '2rem',
        fontSize: '0.875rem',
        color: secondaryTextColor,
        opacity: 0.8,
        textAlign: 'center'
      }}>
        {t('common.previewLabel', 'Preview')}
      </div>
    </div>
  );
};

export default CardPreview;
